import { ElementRef, forwardRef } from 'react'
import * as ProgressPrimitive from '@radix-ui/react-progress'

import { cn } from 'utils/classMerge'
import { translate } from 'utils/translation'

import Text from '../text'

import type { ProgressProps } from './index'

import { StepIndicator } from './step'

export type ProgressBarRef = ElementRef<typeof ProgressPrimitive.Root>

const ProgressIndicator = ({ value }: { value: number }) => (
  <ProgressPrimitive.Indicator
    className="tw-h-full tw-w-full tw-flex-1 tw-bg-orange-500 tw-transition-all tw-duration-500 tw-ease-in-out"
    style={{ transform: `translateX(-${100 - value}%)` }}
    data-testid="progress-indicator"
  />
)

const ProgressBar = forwardRef<ProgressBarRef, ProgressProps>(
  (
    {
      steps,
      progress,
      variant = 'bar',
      className,
      animated = true,
      displayStepNames = false,
      ...props
    },
    ref,
  ) => {
    let progressValue = progress ?? 0

    if (steps?.length) {
      const currentStepIndex = steps.findIndex(
        (step) => step.status === 'current',
      )
      const calculatedProgress = Math.round(
        (currentStepIndex / (steps.length - 1)) * 100,
      )
      progressValue = progress ?? calculatedProgress
    }

    const clampedProgress = Math.min(Math.max(progressValue, 0), 100)

    const progressBar = (
      <ProgressPrimitive.Root
        ref={ref}
        className="tw-relative tw-h-1 tw-w-full tw-overflow-hidden tw-rounded-full tw-bg-zinc-100"
        value={clampedProgress}
        {...props}
        aria-valuenow={clampedProgress}
        aria-valuemin={0}
        aria-valuemax={100}
        data-testid="progress-bar"
      >
        <ProgressIndicator value={clampedProgress} />
      </ProgressPrimitive.Root>
    )

    if (!steps?.length) {
      return <div className={cn('tw-w-full', className)}>{progressBar}</div>
    }

    const currentStep = steps.findIndex((step) => step.status === 'current') + 1

    return (
      <div className={cn('tw-w-full tw-space-y-6', className)}>
        {(variant === 'bullets' || variant === 'both') && (
          <nav
            aria-label="Progress"
            className="tw-flex tw-items-center tw-justify-center tw-h-5 tw-gap-4"
          >
            <Text
              data-testid="bullet-step-heading"
              as="p"
              className="tw-m-0 tw-text-sm tw-font-medium tw-text-zinc-900"
            >
              {translate('step_x_of_y', currentStep, steps.length)}
            </Text>
            <ol className="tw-pl-0 tw-m-0 tw-flex tw-items-center tw-space-x-5">
              {steps.map((step) => (
                <li key={step.name}>
                  <StepIndicator step={step} animated={animated} />
                </li>
              ))}
            </ol>
          </nav>
        )}

        {(variant === 'bar' || variant === 'both') && (
          <div>
            <Text as="h4" className="tw-sr-only">
              status
            </Text>
            <div className="tw-mt-6" aria-hidden="true">
              {progressBar}
              {displayStepNames && (
                <div className="tw-mt-6 tw-hidden tw-grid-cols-4 tw-text-sm tw-font-medium tw-text-muted-foreground sm:tw-grid">
                  {steps.map((step, index) => (
                    <Text
                      as="span"
                      key={step.name}
                      className={cn({
                        'tw-text-left': index === 0,
                        'tw-text-right': index === steps.length - 1,
                        'tw-text-center': index > 0 && index < steps.length - 1,
                        'tw-text-zinc-400': step.status !== 'upcoming',
                        'tw-text-orange-500': step.status === 'current',
                      })}
                    >
                      {step.name}
                    </Text>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    )
  },
)

ProgressBar.displayName = 'ProgressBar'

export default ProgressBar
